// Doors (Admin): the door alarm board, each door, the alert switch, and the last 48 h of door changes
// (jobq-api GET /security/doors, "After-hours security: door sensors"). Wording + calls:
// features/security/doors.js. Every call is securityFetch (the session's Admin PIN): a refused PIN
// (401 / 403) stops polling until the PIN is entered again, since each refused read counts against the
// shop's per-IP guess limit.

import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AdminContext, requestAdminPin } from '../adminSession.jsx';
import { needsAdminPin } from '../utils/securityApi';
import { CAMERA_BASE_URL, fetchRelayJson } from '../utils/cameraRelay';
import { usePolling } from '../components/cameras/useCameraData';
import DoorLocksSection from './DoorLocksSection';
import ShopLightsSection from './ShopLightsSection';
import { shopClock } from '../utils/shopDate';
import {
  DOORS_HOURS,
  DOORS_POLL_MS,
  DOOR_MODES,
  MAX_DOOR_CAMERAS,
  armedText,
  doorCameraChoices,
  doorStateView,
  fetchDoors,
  holdOutcome,
  modeConfirmText,
  modeInfo,
  modeNeedsConfirm,
  panelLine,
  saveDoorZone,
  setDoorsMode,
  stablePhotoUrls,
  toggleCamera,
  zoneEditBody,
} from '../features/security/doors';

const TONE = {
  ok: { bg: '#d1f2dd', fg: '#14532d', border: '#16a34a' },
  bad: { bg: '#fde2e1', fg: '#7f1d1d', border: '#dc2626' },
  warn: { bg: '#fff1cc', fg: '#713f12', border: '#d97706' },
  muted: { bg: '#e9ecef', fg: '#495057', border: '#adb5bd' },
};

const styles = {
  page: { maxWidth: 980 },
  headerCard: { display: 'grid', gap: 8 },
  boardLine: (tone) => ({ fontWeight: 600, color: TONE[tone].fg }),
  dot: (tone) => ({ display: 'inline-block', width: 10, height: 10, borderRadius: 5, background: TONE[tone].border, marginRight: 8 }),
  modeRow: { display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' },
  doorGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 },
  doorCard: (tone) => ({ borderLeft: `6px solid ${TONE[tone].border}` }),
  badge: (tone) => ({ background: TONE[tone].bg, color: TONE[tone].fg, border: `1px solid ${TONE[tone].border}`,
    borderRadius: 6, padding: '2px 10px', fontWeight: 700, fontSize: 14, whiteSpace: 'nowrap' }),
  small: { fontSize: 13 },
  camList: { display: 'grid', gap: 4, maxHeight: 220, overflowY: 'auto' },
  event: { display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, padding: '10px 0', borderTop: '1px solid var(--bs-border-color, #dee2e6)' },
  outcome: (tone) => ({ color: TONE[tone].fg, fontSize: 14 }),
  thumb: { width: 120, maxHeight: 160, objectFit: 'cover', borderRadius: 6, border: '1px solid #ccc', cursor: 'zoom-in', display: 'block' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.85)', zIndex: 1060, display: 'flex',
    alignItems: 'center', justifyContent: 'center', padding: 16, cursor: 'zoom-out' },
  big: { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' },
};

function DoorCard({ zone, choices, onSaved, onRefused }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(zone.name);
  const [keys, setKeys] = useState(zone.camera_keys || []);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const view = doorStateView(zone);

  const open = () => {
    setName(zone.name);
    setKeys(zone.camera_keys || []);
    setMsg(null);
    setEditing(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const body = zoneEditBody(zone, name, keys);
    if (!body) {
      setEditing(false);
      return;
    }
    setBusy(true);
    setMsg(null);
    const r = await saveDoorZone(zone.panel_id, zone.zone_number, body);
    setBusy(false);
    if (needsAdminPin(r)) {
      onRefused(r.message);
      return;
    }
    if (!r.ok) {
      setMsg({ kind: 'danger', text: r.message });
      return;
    }
    setEditing(false);
    setMsg({ kind: 'success', text: 'Saved.' });
    onSaved(r.body);
  };

  const camChoices = choices || [];
  return (
    <div className="card" style={styles.doorCard(zone.is_active ? view.tone : 'muted')} data-testid={`door-${zone.zone_id}`}>
      <div className="card-body">
        <div className="d-flex align-items-start justify-content-between gap-2">
          <div>
            <h5 className="mb-0">{zone.name}</h5>
            <div className="text-muted" style={styles.small}>Zone {zone.zone_number}{zone.is_active ? '' : ' · not watched'}</div>
          </div>
          <span style={styles.badge(view.tone)}>{view.label}</span>
        </div>
        {view.since && <div className="mt-1" style={styles.small}>Since {view.since}</div>}
        <div className="mt-1 text-muted" style={styles.small}>
          {zone.cameras && zone.cameras.length ? `Cameras: ${zone.cameras.join(', ')}` : 'No cameras picked: alerts go without a photo'}
        </div>
        {!editing && (
          <button type="button" className="btn btn-sm btn-outline-secondary mt-2" onClick={open}>Edit</button>
        )}
        {editing && (
          <form className="mt-2" onSubmit={save} style={{ display: 'grid', gap: 8 }}>
            <label className="form-label mb-0" style={styles.small}>
              Name
              <input className="form-control form-control-sm" value={name} maxLength={60}
                     onChange={(e) => setName(e.target.value)} disabled={busy} />
            </label>
            <div style={styles.small}>
              Cameras for the alert photo (up to {MAX_DOOR_CAMERAS}, in this order: {keys.length ? keys.map((k) => (camChoices.find((c) => c.key === k) || { label: k }).label).join(', ') : 'none'})
            </div>
            <div style={styles.camList}>
              {camChoices.length === 0 && (
                <span className="text-muted" style={styles.small}>{choices ? "The camera list didn't load." : 'Loading cameras…'}</span>
              )}
              {camChoices.map((c) => {
                const on = keys.includes(c.key);
                return (
                  <label key={c.key} className="form-check mb-0" style={styles.small}>
                    <input type="checkbox" className="form-check-input" checked={on}
                           disabled={busy || (!on && keys.length >= MAX_DOOR_CAMERAS)}
                           onChange={() => setKeys((k) => toggleCamera(k, c.key))} />
                    <span className="form-check-label">{c.label}</span>
                  </label>
                );
              })}
            </div>
            <div className="d-flex gap-2">
              <button type="submit" className="btn btn-sm btn-primary" disabled={busy || !name.trim()}>{busy ? 'Saving…' : 'Save'}</button>
              <button type="button" className="btn btn-sm btn-outline-secondary" disabled={busy} onClick={() => setEditing(false)}>Cancel</button>
              {keys.length > 0 && (
                <button type="button" className="btn btn-sm btn-link" disabled={busy} onClick={() => setKeys([])}>No cameras</button>
              )}
            </div>
          </form>
        )}
        {msg && <div className={`alert alert-${msg.kind} py-1 px-2 mt-2 mb-0`} style={styles.small}>{msg.text}</div>}
      </div>
    </div>
  );
}

function EventRow({ e, holdSeconds, photo, onOpenPhoto }) {
  const outcome = holdOutcome(e, holdSeconds);
  const armed = armedText(e);
  return (
    <div style={styles.event} data-testid={`door-event-${e.event_id}`}>
      <div>
        <div>
          <strong>{shopClock(e.at)}</strong>{' · '}{e.name}{' '}
          <span style={{ color: e.state === 'open' ? TONE.bad.fg : TONE.ok.fg, fontWeight: 600 }}>
            {e.state === 'open' ? 'opened' : 'closed'}
          </span>
          {e.source === 'beat' && <span className="text-muted" style={styles.small}> (seen in the board's minute report)</span>}
        </div>
        {armed && <div className="text-muted" style={styles.small}>{armed}</div>}
        {outcome && <div style={styles.outcome(outcome.tone)}>{outcome.text}</div>}
        {e.errors && <div style={{ ...styles.small, color: TONE.bad.fg }}>Problems: {e.errors}</div>}
        {e.photo_note && <div className="text-muted" style={{ fontSize: 12 }}>{e.photo_note}</div>}
      </div>
      {photo && (
        <button type="button" className="btn p-0 border-0" onClick={() => onOpenPhoto(photo.url)}
                aria-label={`Photo of ${e.name} at ${shopClock(e.at)}: open full size`}>
          <img src={photo.url} alt="" loading="lazy" style={styles.thumb} />
        </button>
      )}
    </div>
  );
}

export default function DoorsPage() {
  const admin = useContext(AdminContext);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [pinNeeded, setPinNeeded] = useState(false);
  const [modeBusy, setModeBusy] = useState(false);
  const [modeMsg, setModeMsg] = useState(null);
  const [cameraConfig, setCameraConfig] = useState(null);
  const [bigPhoto, setBigPhoto] = useState(null);
  const [photos, setPhotos] = useState({});
  const photosRef = useRef({});

  const refused = useCallback((message) => {
    setPinNeeded(true);
    setError(message || 'JobQ refused the Admin PIN.');
  }, []);

  const load = useCallback(async () => {
    const r = await fetchDoors();
    if (needsAdminPin(r)) {
      refused(r.message);
      return;
    }
    if (!r.ok) {
      setError(r.message);
      return;
    }
    setError(null);
    setData(r.body);
    photosRef.current = stablePhotoUrls(photosRef.current, r.body && r.body.events);
    setPhotos(photosRef.current);
  }, [refused]);

  // Every 30 s while the tab is visible; never while the PIN is refused.
  usePolling(load, DOORS_POLL_MS, !pinNeeded);

  // The relay's camera list, once (for the Edit choices).
  useEffect(() => {
    let live = true;
    fetchRelayJson(`${CAMERA_BASE_URL}/cameras/config`)
      .then((c) => { if (live) setCameraConfig(c || {}); })
      .catch(() => { if (live) setCameraConfig({}); });
    return () => { live = false; };
  }, []);

  useEffect(() => {
    if (!bigPhoto) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setBigPhoto(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [bigPhoto]);

  const unlock = async () => {
    const user = await requestAdminPin();
    if (!user) return;
    if (admin && admin.loginAsAdmin) {
      admin.loginAsAdmin({ userId: user.userId, userName: user.userName, role: user.role, pin: user.pin },
        { remember: user.remember });
    } else {
      sessionStorage.setItem('userRole', String(user.role || '').toLowerCase());
      sessionStorage.setItem('adminPin', user.pin);
    }
    setError(null);
    setPinNeeded(false); // polling starts again (one read now)
  };

  const switchMode = async (mode) => {
    if (!data || mode === data.mode) return;
    // eslint-disable-next-line no-alert
    if (modeNeedsConfirm(mode) && !window.confirm(modeConfirmText(mode))) return;
    setModeBusy(true);
    setModeMsg(null);
    const r = await setDoorsMode(mode);
    setModeBusy(false);
    if (needsAdminPin(r)) {
      refused(r.message);
      return;
    }
    if (!r.ok) {
      setModeMsg({ kind: 'danger', text: r.message });
      return;
    }
    const now = (r.body && r.body.mode) || mode;
    const info = modeInfo(now);
    setModeMsg({ kind: 'success', text: `Door alerts are now ${info ? info.label : now}. ${info ? info.what : ''}`.trim() });
    load();
  };

  const zoneSaved = (z) => {
    setData((d) => (d ? { ...d, zones: (d.zones || []).map((x) => (x.zone_id === z.zone_id ? { ...x, ...z } : x)) } : d));
  };

  const zones = (data && data.zones) || [];
  const ordered = [...zones.filter((z) => z.is_active), ...zones.filter((z) => !z.is_active)];
  const allKeys = zones.flatMap((z) => z.camera_keys || []);
  const choices = cameraConfig ? doorCameraChoices(cameraConfig, allKeys) : null;
  const panels = (data && data.panels) || [];
  const events = (data && data.events) || [];
  const mode = data && data.mode;
  const info = modeInfo(mode);

  return (
    <div className="container py-4" style={styles.page}>
      <div className="d-flex flex-wrap align-items-baseline justify-content-between gap-2 mb-2">
        <h2 className="mb-0">Doors</h2>
        <div className="d-flex gap-3" style={styles.small}>
          <Link to="/check-in">Crew check-in screen</Link>
          <Link to="/security-alerts">Who gets texts</Link>
          <Link to="/cameras?mode=alerts">Camera alerts</Link>
        </div>
      </div>
      <p className="text-muted mb-3" style={styles.small}>
        The door alarm board reports each door to JobQ. When the shop is armed and a door opens, JobQ waits
        {' '}{data ? Math.round((data.hold_seconds || 120) / 60) : 2} min for someone to check in, then sends the alert.
      </p>

      {error && (
        <div className="alert alert-danger d-flex flex-wrap align-items-center gap-2">
          <span>{pinNeeded ? `This page needs an Admin PIN. ${error}` : error}</span>
          {pinNeeded && (
            <button type="button" className="btn btn-sm btn-outline-danger" onClick={unlock}>Enter Admin PIN</button>
          )}
        </div>
      )}
      {!data && !error && <div className="text-muted">Loading…</div>}

      {data && (
        <>
          <div className="card mb-3">
            <div className="card-body" style={styles.headerCard}>
              {panels.length === 0 && <div className="text-muted">No door board is set up yet.</div>}
              {panels.map((p) => {
                const line = panelLine(p);
                return (
                  <div key={p.panel_id} style={styles.boardLine(line.tone)} data-testid={`panel-${p.panel_id}`}>
                    <span style={styles.dot(line.tone)} />{line.text}
                  </div>
                );
              })}
              <div style={styles.modeRow}>
                <span style={styles.small}>Alerts:</span>
                <div className="btn-group" role="group" aria-label="Door alert mode">
                  {DOOR_MODES.map((m) => (
                    <button key={m.mode} type="button" disabled={modeBusy}
                            className={`btn btn-sm ${mode === m.mode ? 'btn-dark' : 'btn-outline-dark'}`}
                            aria-pressed={mode === m.mode} onClick={() => switchMode(m.mode)}>
                      {m.label}
                    </button>
                  ))}
                </div>
                <span className="text-muted" style={styles.small}>{info ? info.what : `Mode: ${mode || 'unknown'}`}</span>
              </div>
              {modeMsg && <div className={`alert alert-${modeMsg.kind} py-1 px-2 mb-0`} style={styles.small} role="status">{modeMsg.text}</div>}
            </div>
          </div>

          <DoorLocksSection onRefused={refused} paused={pinNeeded} />
          <ShopLightsSection onRefused={refused} paused={pinNeeded} />

          <h4 className="mb-2">Doors</h4>
          {ordered.length === 0 && <div className="text-muted mb-3">No doors are named yet.</div>}
          <div style={styles.doorGrid} className="mb-4">
            {ordered.map((z) => (
              <DoorCard key={z.zone_id} zone={z} choices={choices} onSaved={zoneSaved} onRefused={refused} />
            ))}
          </div>

          <h4 className="mb-1">Last {DOORS_HOURS} hours</h4>
          {events.length === 0 && <div className="text-muted">No door changes.</div>}
          <div>
            {events.map((e) => (
              <EventRow key={e.event_id} e={e} holdSeconds={data.hold_seconds} photo={photos[e.event_id]} onOpenPhoto={setBigPhoto} />
            ))}
          </div>
        </>
      )}

      {bigPhoto && (
        <div style={styles.overlay} role="dialog" aria-modal="true" aria-label="Door photo" onClick={() => setBigPhoto(null)}>
          <img src={bigPhoto} alt="Door photo, full size" style={styles.big} />
        </div>
      )}
    </div>
  );
}
